import React, { useState } from 'react'
import OperationWorkflowModal from 'common/components/OperationWorkflowModal'
import API from 'common/logic/API'
import { Button, Col, Form } from 'react-bootstrap'
import { Formik } from 'formik'
import * as yup from 'yup'
import * as Types from 'common/utils/Types'
import { Navigate } from 'react-router-dom'
import Utils from "common/Utils"
import { Movement, Tag } from 'types'
import TagSelect from 'common/components/TagSelect'

interface Props {
    onSuccess: string | ((result: unknown) => void)
    static?: boolean
    entryId?: number
    prefill?: Partial<Movement> & { suggestedTagIds?: number[] }
}

interface FormProps {
    loadedFunds: any[]
    loadedTags: Tag[]
    previousValues: any
    prefill?: Partial<Movement> & { suggestedTagIds?: number[] }
    completion: (input: unknown) => void
}

function MovementForm({ loadedFunds, loadedTags, previousValues, prefill, completion }: FormProps) {
    const initialTagIds: number[] = previousValues?.tags
        ? previousValues.tags.map((t: Tag) => t.id)
        : prefill?.suggestedTagIds ?? []

    const [tagIds, setTagIds] = useState<number[]>(initialTagIds)
    const [availableTags, setAvailableTags] = useState<Tag[]>(loadedTags)

    const parseForm = (values: any) => {
        completion({
            description: values.description,
            statementEntry: values.statementEntry.trim(),
            notes: values.notes || null,
            fundId: values.fund,
            date: values.date,
            accountableDate: values.accountableDate,
            amount: values.amount.trim(),
            tagIds
        })
    }

    const schema = yup.object().shape({
        description: yup.string().trim(),
        amount: yup.number().required(),
        date: yup.date().required(),
        accountableDate: yup.date().required(),
        fund: yup.number().required()
    })

    const funds = loadedFunds
        .filter((f: any) => (f.usedForNewMovements && !f.deleted) || (previousValues && f.id === previousValues.fund.id))

    const fund_options = funds.map((item: any) => <option value={item.id} key={`fund${item.id}`}>{item.name}</option>)

    const today = Utils.formatDate(new Date())

    const initialValues = previousValues ? {
        date: previousValues.date,
        accountableDate: previousValues.accountableDate,
        description: previousValues.description || "",
        statementEntry: previousValues.statementEntry || "",
        notes: previousValues.notes || "",
        amount: previousValues.amount,
        fund: previousValues.fund.id
    } : {
        date: today,
        accountableDate: today,
        description: "",
        statementEntry: "",
        notes: "",
        amount: "",
        fund: funds[0].id,
    }

    return (
        <Formik validationSchema={schema} onSubmit={parseForm} initialValues={initialValues}>
            {({ handleSubmit, handleChange, values, errors }) => (
                <Form noValidate onSubmit={handleSubmit}>
                    <Form.Group controlId="formDescription">
                        <Form.Label column sm="2">Description</Form.Label>
                        <Form.Control
                            type='text'
                            name='description'
                            placeholder="Description"
                            value={values.description}
                            onChange={handleChange}
                            isInvalid={!!errors.description}
                        />
                        <Form.Control.Feedback type="invalid">{errors.description}</Form.Control.Feedback>
                        <Form.Control
                            type='text'
                            name='statementEntry'
                            placeholder="Statement entry"
                            value={values.statementEntry}
                            onChange={handleChange}
                            isInvalid={!!errors.statementEntry}
                        />
                        <Form.Control.Feedback type="invalid">{errors.statementEntry}</Form.Control.Feedback>
                    </Form.Group>

                    <Form.Row>
                        <Form.Group as={Col} controlId="formAmount">
                            <Form.Label column sm="2">Amount</Form.Label>
                            <Form.Control
                                type='text'
                                name='amount'
                                placeholder="-10.00"
                                value={values.amount}
                                onChange={handleChange}
                                isInvalid={!!errors.amount}
                            />
                            <Form.Control.Feedback type="invalid">{errors.amount}</Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group as={Col} controlId="formDate">
                            <Form.Label column sm="2">Date</Form.Label>
                            <Form.Control
                                type='text'
                                name='date'
                                placeholder="2020-12-31"
                                value={values.date}
                                onChange={handleChange}
                                isInvalid={!!errors.date}
                            />
                            <Form.Control.Feedback type="invalid">{errors.date}</Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group as={Col} controlId="formAccountableDate">
                            <Form.Label column sm="2">Statement date</Form.Label>
                            <Form.Control
                                type='text'
                                name='accountableDate'
                                placeholder="2020-12-31"
                                value={values.accountableDate}
                                onChange={handleChange}
                                isInvalid={!!errors.accountableDate}
                            />
                            <Form.Control.Feedback type="invalid">{errors.accountableDate}</Form.Control.Feedback>
                        </Form.Group>
                    </Form.Row>

                    <Form.Row>
                        <Form.Group as={Col} controlId="formFund">
                            <Form.Label column sm="2">Fund</Form.Label>
                            <Form.Control
                                as='select'
                                name='fund'
                                value={values.fund}
                                onChange={handleChange}
                                isInvalid={!!errors.fund}
                            >
                                {fund_options}
                            </Form.Control>
                            <Form.Control.Feedback type="invalid">{errors.fund}</Form.Control.Feedback>
                        </Form.Group>
                    </Form.Row>

                    <Form.Group controlId="formTags">
                        <Form.Label>Tags</Form.Label>
                        <TagSelect
                            selectedTagIds={tagIds}
                            availableTags={availableTags}
                            onChange={(ids, updatedTags) => {
                                setTagIds(ids)
                                setAvailableTags(updatedTags)
                            }}
                        />
                    </Form.Group>

                    <Form.Group controlId="formNotes">
                        <Form.Label>Notes</Form.Label>
                        <Form.Control
                            as='textarea'
                            name='notes'
                            placeholder="Notes"
                            value={values.notes}
                            onChange={handleChange}
                            rows={3}
                        />
                    </Form.Group>

                    <Button variant="primary" type="submit">Save</Button>
                </Form>
            )}
        </Formik>
    )
}

function EditMovementWorkflow(props: Props) {

    const [redirect, setRedirect] = useState<string | null>(null)

    if (redirect) {
        return <Navigate to={redirect} replace />
    }

    const loadingStep = (_parameters: unknown) => {
        const dataToFetch: Promise<any>[] = [
            API.get('/funds').then(response => (response.data as any)["results"]),
            API.get('/tags').then(response => (response.data as any)["results"])
        ]
        if (props.entryId !== undefined) {
            dataToFetch.push(API.get(`/movements/${props.entryId}`).then(response => response.data))
        }
        return Promise.all(dataToFetch)
            .then((arr) => ({
                funds: arr[0],
                tags: arr[1],
                movement: arr[2] || props.prefill || null
            }))
    }

    const userInputStep = (_parameters: unknown, loading_result: unknown, _values: unknown, _errors: unknown, completion: (input: unknown) => void) => {
        const lr = loading_result as any
        return {
            component: (
                <MovementForm
                    loadedFunds={lr.funds}
                    loadedTags={lr.tags}
                    previousValues={lr.movement}
                    prefill={props.prefill}
                    completion={completion}
                />
            ),
            title: props.entryId ? "Edit movement" : "New movement"
        }
    }

    const executionStep = (_parameters: unknown, _loading_result: unknown, user_input: unknown) => {
        if (props.entryId) {
            return API.put(`/movements/${props.entryId}`, user_input)
        } else {
            return API.post("/movements", user_input)
        }
    }

    const completionCallback = (execution_result: unknown) => {
        if (Types.isString(props.onSuccess)) {
            setRedirect(props.onSuccess as string)
        } else {
            (props.onSuccess as (result: unknown) => void)(execution_result && (execution_result as any).data)
        }
    }

    return <OperationWorkflowModal
        static={props.static}
        loadingStep={loadingStep}
        userInputStep={userInputStep}
        executionStep={executionStep}
        completionCallback={completionCallback}
    />
}

export default EditMovementWorkflow
